import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { Link, useHistory } from 'react-router-dom'
import {
    BuildingMultipleRegular,
    bundleIcon,
    CalendarAddFilled,
    CalendarAddRegular,
    DocumentAddFilled,
    DocumentAddRegular,
    EditFilled,
    EditRegular,
    MailRegular,
    PrintFilled,
    PrintRegular,
} from '@fluentui/react-icons'
import { Button, Card, makeStyles, mergeClasses, Text, tokens, Tooltip } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { ViewCRMProps } from './ViewCRM'
import { PartyType } from '../../../components/Form/Party/PartyForm'
import { LegalPersonDto } from '../../../config/partyConfig'
import PhoneNumberButton from '../../components/Button/PhoneNumberButton'
import EmailButton from '../../components/Button/EmailButton'
import { composeEmailClick } from '../../../components/List/FileParty/FilePartyList'
import { useApplicationStyles } from '../../../pages/App'
import { isOfficeInitialized, outlookDisplayNewAppointmentForm } from '../../../helpers/outlookHelper'
import { formatRecipients } from '../../components/Dialog/OutlookItem/ComposeOutlookItemDialogBody'
import { isAppContext } from '../../../helpers/appHelper'
import { AppContext } from '../../../config/appConfig'
import OfficeDocumentDialogBody, {
    getOfficeDocumentDialogBodySchema,
    OfficeDocumentDialogBodyProps,
    OfficeDocumentDialogBodyValues,
} from '../../components/Dialog/DriveItem/OfficeDocumentDialogBody'
import { DocumentType } from '../../../helpers/documentHelper'
import { DocumentContext } from '../../../config/documentConfig'
import HammockFormDialog from '../../components/Dialog/HammockFormDialog'

const CalendarAddIcon = bundleIcon(CalendarAddFilled, CalendarAddRegular)
const DocumentAddIcon = bundleIcon(DocumentAddFilled, DocumentAddRegular)
const EditIcon = bundleIcon(EditFilled, EditRegular)
const PrintIcon = bundleIcon(PrintFilled, PrintRegular)

const useStyles = makeStyles({
    headerGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingHorizontalM,

        '@media screen and (max-width: 550px)': {
            gridTemplateColumns: '1fr',
        },
    },
    formContainer: {
        display: 'grid',
        gridTemplateColumns: '25% 1fr',
        gap: tokens.spacingHorizontalS,
    },
    gridSpan2Columns: {
        gridColumn: 'span 2',
    },
    sectionHeader: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    actionsRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: tokens.spacingHorizontalS,
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
})

export default function ViewCRMHeader(props: ViewCRMProps): ReactElement {
    const appStyles = useApplicationStyles()
    const history = useHistory()
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const editUrl = useMemo(() => {
        return props.crmRelation?.type === PartyType.Legal
            ? `/crm/${props.crmRelationId}/legal/${props.crmRelationId}/edit`
            : `/crm/${props.crmRelationId}/natural/${props.crmRelationId}/edit`
    }, [props.crmRelation?.type, props.crmRelationId])

    const legalPerson = useMemo(
        () => (props.crmRelation.type === PartyType.Legal ? (props.crmRelation as LegalPersonDto) : undefined),
        [props.crmRelation],
    )

    return (
        <Card className={styles.headerGrid}>
            <div className={styles.formContainer}>
                <Text weight={'semibold'} size={400} className={styles.gridSpan2Columns}>
                    {props.crmRelation?.displayName}
                </Text>
                {props.crmRelation?.address && (
                    <div className={styles.gridSpan2Columns}>
                        <BuildingMultipleRegular />
                        <Text>
                            {props.crmRelation.address.street} {props.crmRelation.address.number}, {props.crmRelation.address.postalCode}{' '}
                            {props.crmRelation.address.locality}, {props.crmRelation.address.country}
                        </Text>
                    </div>
                )}
                {legalPerson?.poBoxAddress && (
                    <div className={styles.gridSpan2Columns}>
                        <MailRegular />
                        <Text>
                            {legalPerson?.poBoxAddress?.street} {legalPerson?.poBoxAddress?.number}, {legalPerson?.poBoxAddress?.postalCode}{' '}
                            {legalPerson?.poBoxAddress?.locality}, {legalPerson?.poBoxAddress?.country}
                        </Text>
                    </div>
                )}
                {legalPerson?.cocNumber && (
                    <>
                        <div>{translate('crm.detail.coc')}</div>
                        <Text>{legalPerson?.cocNumber}</Text>
                    </>
                )}
                {legalPerson?.vatNumber && (
                    <>
                        <div>{translate('crm.detail.vat')}</div>
                        <Text>{legalPerson?.vatNumber}</Text>
                    </>
                )}
                {legalPerson?.financeEmail && (
                    <>
                        <div>{translate('crm.detail.financeemail')}</div>
                        <Text>{legalPerson?.financeEmail}</Text>
                    </>
                )}
                {props.crmRelation?.kycFile && (
                    <>
                        <div>{translate('crm.detail.kyc')}</div>
                        <Link to={`/file/${props.crmRelation.kycFile?.id}`}>
                            <Text>
                                {props.crmRelation?.kycFile.reference} - {props.crmRelation?.kycFile.name}
                            </Text>
                        </Link>
                    </>
                )}
            </div>
            <div>
                {props.contacts?.map((contact, index) => (
                    <div key={index} className={styles.formContainer}>
                        <Link to={`/crm/${props.crmRelation.id}/natural/${contact.id}/edit`} className={styles.gridSpan2Columns}>
                            <Text weight={'semibold'} size={400}>
                                {contact.displayName}
                            </Text>
                        </Link>

                        {contact.phoneLandline && (
                            <div className={appStyles.selfStart}>
                                <PhoneNumberButton phoneNumber={contact.phoneLandline} />
                            </div>
                        )}
                        {contact.phoneMobile && (
                            <div className={appStyles.selfStart}>
                                <PhoneNumberButton phoneNumber={contact.phoneMobile} />
                            </div>
                        )}
                        {[contact.phoneMobile, contact.phoneMobile].filter(Boolean).length === 1 && <div></div>}
                        {contact.email && (
                            <div className={mergeClasses(appStyles.selfStart, styles.gridSpan2Columns)}>
                                <EmailButton
                                    className={appStyles.selfStart}
                                    email={contact.email}
                                    composeEmailClick={() => composeEmailClick(contact)()}
                                />
                            </div>
                        )}
                        {isOfficeInitialized() && !isAppContext(AppContext.Compose) && (
                            <div>
                                <Tooltip relationship={'label'} content={translate('action.create.appointment')}>
                                    <Button
                                        size={'small'}
                                        appearance={'transparent'}
                                        icon={<CalendarAddIcon />}
                                        onClick={() => {
                                            outlookDisplayNewAppointmentForm({
                                                requiredAttendees: formatRecipients([contact]),
                                                subject: props.crmRelation.displayName,
                                            })
                                        }}
                                    />
                                </Tooltip>
                            </div>
                        )}
                        <HammockFormDialog<Partial<OfficeDocumentDialogBodyValues>, OfficeDocumentDialogBodyProps>
                            tooltipProps={{ content: translate('file.pivot.document.create.standard') }}
                            triggerComponent={() => <Button size={'small'} appearance={'transparent'} icon={<DocumentAddIcon />} />}
                            dialogFormProps={() => ({
                                initialValues: {
                                    type: DocumentType.Word,
                                },
                                onSubmit: () => {},
                                validationSchema: getOfficeDocumentDialogBodySchema(translate),
                            })}
                            dialogFormBody={OfficeDocumentDialogBody}
                            dialogFormBodyProps={() => ({
                                context: DocumentContext.Party,
                                documentRootId: props.crmRelation.documentRootId,
                            })}
                        />
                    </div>
                ))}
            </div>

            <div className={styles.actionsRow}>
                {props.crmRelation?.type === PartyType.Legal && (
                    <Tooltip content={translate('crm.detail.printcard')} relationship={'description'} withArrow>
                        <Button icon={<PrintIcon />} appearance={'subtle'} />
                    </Tooltip>
                )}
                <Tooltip content={translate('crm.detail.edit')} relationship={'description'} withArrow>
                    <Button icon={<EditIcon />} appearance={'subtle'} onClick={() => history.push(editUrl)} />
                </Tooltip>
            </div>
        </Card>
    )
}
